import React from 'react';
import { useForm } from 'react-hook-form';

import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IGPSTracker } from '@model/gpsTracker';

import { useGPSTrackerSaveMutation } from '@pages/GPSAssetTracking/services/useGPSTrackerSaveMutation';
import { useAssetTrackerActions } from '@pages/GPSAssetTracking/store/useAssetTrackerStore';

import { selectSecondaryModal, useModalActions } from '@store/useModalStore';

import ModalHeader from '../ModalHeader/ModalHeader';
import {
  GPS_TRACKER_FIELDS,
  type IFormGPSTracker,
  gpsTrackerDefaultValues,
  schemaGPSTracker,
} from './constants/schemaGpsTracker';
import style from './style.module.scss';

interface IProps {
  gpsTracker: IGPSTracker;
  onClose: () => void;
}

const CreateEditGpsTrackerModal: React.FC<IProps> = ({ gpsTracker, onClose }) => {
  const { t } = useTranslation();

  const { setModalClose, setSecondaryModalClose } = useModalActions();
  const { setAssetCreatedTracker } = useAssetTrackerActions();
  const secondaryModal = selectSecondaryModal();

  const { mutate: gpsTrackerCreateMutation, isLoading: isGpsTrackerCreateLoading } =
    useGPSTrackerSaveMutation({ gpsTrackerId: gpsTracker?.id });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<IFormGPSTracker>({
    mode: 'all',
    resolver: schemaGPSTracker,
    defaultValues: gpsTrackerDefaultValues(gpsTracker),
  });

  const handleClose = (newTracker?: IGPSTracker) => {
    reset();

    if (secondaryModal?.name) {
      if (newTracker) {
        setAssetCreatedTracker(newTracker);
      }

      setSecondaryModalClose();
    } else {
      setModalClose();
    }

    if (onClose) {
      onClose();
    }
  };

  const handleSave = handleSubmit((data) =>
    gpsTrackerCreateMutation(data, {
      onSuccess: ({ data }) => {
        showToast({ variant: 'success', message: t('Your changes have been saved.') });

        handleClose(data.data);
      },
    }),
  );

  return (
    <div className={style.createGpsTrackerModal}>
      <ModalHeader onClose={handleClose} />
      <div className={style.modalBody}>
        <div className={style.modalBodyTitle}>
          {t(gpsTracker?.id ? 'Edit GPS Tracker' : 'Add new GPS Tracker')}
        </div>
        <div className={style.formFields}>
          <Input name={GPS_TRACKER_FIELDS.MODEL} control={control} label={t('Model')} required />
          <Input
            name={GPS_TRACKER_FIELDS.SERIAL_NUMBER}
            control={control}
            label={t('Serial Number')}
            disabled={!!gpsTracker?.id}
            required
          />
          <Input name={GPS_TRACKER_FIELDS.IMEI} control={control} label={t('IMEI')} />
          <Input name={GPS_TRACKER_FIELDS.ICCID} control={control} label={t('ICCID')} />
        </div>
      </div>
      <div className={style.modalFooter}>
        <Button
          type="primary"
          text={t('Save')}
          disabled={!isDirty || isGpsTrackerCreateLoading}
          onClick={handleSave}
        />
        <Button type="secondary" text={t('Cancel')} onClick={handleClose} />
      </div>
    </div>
  );
};

export default CreateEditGpsTrackerModal;
